import { Fragment, useState } from "react";
import { useRefData } from "../lib/refdata";
import { Field, Select, money } from "./ui";

/** Insurance coverage: a base plan plus riders (term / CI), one premium for all. Shared by
 *  "Add solution" on a client and the solutions in a pipeline deal. Types come from lists
 *  (Settings → Lists: PermanentTypes, TermTypes, CITypes, PayPeriods). */
export type Cover = "permanent" | "term" | "ci";
export const COVER_LABEL: Record<Cover, string> = { permanent: "Permanent", term: "Term", ci: "Critical illness" };
const LIST: Record<Cover, string> = { permanent: "PermanentTypes", term: "TermTypes", ci: "CITypes" };

export const baseOf = (t: string | null | undefined): Cover | null =>
  !t ? null : /critical/i.test(t) ? "ci" : /term|mortgage/i.test(t) ? "term" : /permanent|whole|par|universal/i.test(t) ? "permanent" : null;
export const has = (v: string | null | undefined) => !!v && v !== "-";
/** Is this kind of solution insurance? (any wording: "Life insurance · Term", "Term Life", "Critical Illness", "UL"…) */
export const isInsuranceType = (t: string | null | undefined) =>
  !!t && /insurance|\blife\b|\bterm\b|universal|whole|\bpar\b|critical|disability|health|dental|travel|benefit|mortgage|\bul\b|\bci\b/i.test(t);
export type Rider = { kind: Cover; type?: string | null; amount?: number | string | null; renewal_date?: string | null; expiry_date?: string | null };
export const isApplication = (s: string | null | undefined) => ["Submitted", "In underwriting", "Approved – not placed", "Pending"].includes(s ?? "");
export const APP_STATUSES = ["Submitted", "In underwriting", "Approved – not placed"];

const short = (n?: number | string | null) => { const v = Number(n); return !n ? "" : v >= 1_000_000 ? `$${(v / 1_000_000).toFixed(v % 1_000_000 ? 1 : 0)}M` : v >= 1000 ? `$${Math.round(v / 1000)}k` : money(v); };

type Row = Record<string, any>;
/** "UL $250k + T20 $500k rider + CI $50k rider · pay 20 years" */
export function coverText(h: Row) {
  const b = baseOf(h.solution_type);
  const part: Record<Cover, string | null> = {
    permanent: has(h.permanent_type) || h.permanent_amount ? `${has(h.permanent_type) ? h.permanent_type : "Permanent"} ${short(h.permanent_amount)}`.trim() : null,
    term: has(h.term_type) || h.term_amount ? `${has(h.term_type) ? h.term_type : "Term"} ${short(h.term_amount)}`.trim() : null,
    ci: has(h.ci_type) || h.ci_amount ? `${has(h.ci_type) ? h.ci_type : "CI"} ${short(h.ci_amount)}`.trim() : null,
  };
  const order: Cover[] = b ? [b, ...(["permanent", "term", "ci"] as Cover[]).filter((c) => c !== b)] : ["permanent", "term", "ci"];
  const out = order.filter((c) => part[c]).map((c) => (b && c !== b ? `+ ${part[c]} rider` : part[c]!));
  for (const r of (h.riders ?? []) as Rider[]) if (r.type || r.amount) out.push(`+ ${r.type || COVER_LABEL[r.kind]} ${short(r.amount)} rider`.replace(/\s+rider$/, " rider"));
  if (h.other_riders) out.push(`+ ${h.other_riders}`);
  return (out.join(" ") || "—") + (h.premium_pay_period ? ` · pay ${h.premium_pay_period.toLowerCase().replace(/^pay /, "")}` : "");
}

/** A list choice; a typed value that isn't on the list is kept and marked so it can be fixed.
 *  With `other`, "Other…" lets you type your own (e.g. a 12-year pay period). */
export function ListSelect({ list, value, onChange, placeholder, other }: { list: string; value: string | null | undefined; onChange: (v: string) => void; placeholder?: string;
  other?: { label: string; unit?: string; numeric?: boolean } }) {
  const ref = useRefData();
  const opts = ref.data?.list(list) ?? [];
  const custom = has(value) && !opts.includes(value!);
  const [typing, setTyping] = useState(false);
  if (other && (typing || custom)) {
    const n = other.unit && value ? value.replace(new RegExp(`\\s*${other.unit}$`), "") : value ?? "";
    return (
      <div style={{ display: "flex", gap: 6 }}>
        <input className="input" type={other.numeric ? "number" : "text"} min={1} autoFocus={typing} value={n} placeholder={other.label}
          onChange={(e) => onChange(e.target.value ? (other.unit ? `${e.target.value} ${other.unit}` : e.target.value) : "")} />
        {other.unit && <span className="small muted" style={{ alignSelf: "center" }}>{other.unit}</span>}
        <button type="button" className="btn small ghost" onClick={() => { setTyping(false); onChange(""); }}>List</button>
      </div>
    );
  }
  const extra = custom ? [{ value: value!, label: `${value} (typed: choose from the list)` }] : [];
  return <Select value={has(value) ? value : ""} onChange={(v) => (v === "__other" ? setTyping(true) : onChange(v))}
    options={[...extra, ...opts, ...(other ? [{ value: "__other", label: "Other… (type it)" }] : [])]} placeholder={placeholder ?? "Choose…"} />;
}

/** The coverage part of a form. `f` holds the holding-style fields; `set(k)(v)` updates one.
 *  The first rider of each kind uses its columns; more of the same kind (a T10 and a T20) go in `riders`. */
export function CoverageFields({ f, set, dates }: { f: Row; set: (k: string) => (v: any) => void; dates?: boolean }) {
  const base = baseOf(f.solution_type);
  const kinds: Cover[] = base ? [base, ...(["permanent", "term", "ci"] as Cover[]).filter((c) => c !== base)] : ["permanent", "term", "ci"];
  const [openR, setOpenR] = useState<Partial<Record<Cover, boolean>>>({});
  const riders: Rider[] = Array.isArray(f.riders) ? f.riders : [];
  const shown: Record<Cover, boolean> = {
    permanent: !!openR.permanent || has(f.permanent_type) || !!f.permanent_amount,
    term: !!openR.term || has(f.term_type) || !!f.term_amount,
    ci: !!openR.ci || has(f.ci_type) || !!f.ci_amount,
  };
  const setRiders = (v: Rider[]) => set("riders")(v);
  const add = (c: Cover) => (c !== base && !shown[c] ? setOpenR((x) => ({ ...x, [c]: true })) : setRiders([...riders, { kind: c }]));
  const date = (k: string, label: string) => <Field key={k} label={label}><input type="date" className="input" value={f[k] ?? ""} onChange={(e) => set(k)(e.target.value)} /></Field>;
  const primary = (c: Cover) => {
    const r = base && c !== base ? " rider" : "";
    return <Fragment key={c}>
      <Field label={`${COVER_LABEL[c]}${r}: plan`}><ListSelect list={LIST[c]} value={f[`${c}_type`]} onChange={set(`${c}_type`)} /></Field>
      <Field label={`${COVER_LABEL[c]}${r}: amount ($)`}><input type="number" className="input" value={f[`${c}_amount`] ?? ""} onChange={(e) => set(`${c}_amount`)(e.target.value)} /></Field>
      {dates && c === "term" && <>{date("term_renewal_date", `Term${r}: renewal date`)}{date("term_expiry_date", `Term${r}: expiry`)}
        {date("conversion_term_date", "Convert term → term by")}{date("conversion_perm_date", "Convert term → permanent by")}</>}
      {dates && c === "ci" && <>{date("ci_renewal_date", `CI${r}: renewal`)}{date("ci_expiry_date", `CI${r}: expiry`)}</>}
    </Fragment>;
  };
  const extra = (r: Rider, i: number) => {
    const upd = (k: keyof Rider, v: any) => setRiders(riders.map((x, j) => (j === i ? { ...x, [k]: v === "" ? null : v } : x)));
    return <Fragment key={`r${i}`}>
      <Field label={`Another ${COVER_LABEL[r.kind].toLowerCase()} rider: plan`}><ListSelect list={LIST[r.kind]} value={r.type} onChange={(v) => upd("type", v)} /></Field>
      <Field label="Amount ($)"><div style={{ display: "flex", gap: 6 }}><input type="number" className="input" value={r.amount ?? ""} onChange={(e) => upd("amount", e.target.value)} />
        <button type="button" className="btn small ghost" aria-label="Remove this rider" onClick={() => setRiders(riders.filter((_, j) => j !== i))}>Remove</button></div></Field>
      {dates && r.kind !== "permanent" && <>
        <Field label="Renewal date"><input type="date" className="input" value={r.renewal_date ?? ""} onChange={(e) => upd("renewal_date", e.target.value)} /></Field>
        <Field label="Expiry"><input type="date" className="input" value={r.expiry_date ?? ""} onChange={(e) => upd("expiry_date", e.target.value)} /></Field>
      </>}
    </Fragment>;
  };
  return <>
    {base && <h4 className="wide" style={{ margin: "8px 0 0" }}>Base plan</h4>}
    {base && primary(base)}
    {base && riders.map((r, i) => (r.kind === base ? extra(r, i) : null))}
    <h4 className="wide" style={{ margin: "8px 0 0" }}>{base ? "Riders on this policy (paid by the same premium)" : "Coverage"}</h4>
    {kinds.filter((c) => c !== base).map((c) => <Fragment key={c}>{shown[c] && primary(c)}{riders.map((r, i) => (r.kind === c ? extra(r, i) : null))}</Fragment>)}
    <div className="wide toolbar" style={{ margin: 0 }}>
      {kinds.map((c) => <button key={c} type="button" className="btn small" onClick={() => add(c)}>+ {c === base ? `Another ${COVER_LABEL[c].toLowerCase()}` : COVER_LABEL[c]} rider</button>)}
    </div>
    <Field label="Other riders (child term, waiver of premium, ADB…)" wide><input className="input" value={f.other_riders ?? ""} onChange={(e) => set("other_riders")(e.target.value)} /></Field>
  </>;
}

/** Premium, how often, and for how long. */
export function PremiumFields({ f, set }: { f: Row; set: (k: string) => (v: any) => void }) {
  const ref = useRefData();
  return <>
    <Field label="Premium ($): base plan and all riders"><input type="number" className="input" value={f.premium ?? ""} onChange={(e) => set("premium")(e.target.value)} /></Field>
    <Field label="Paid"><Select value={f.premium_frequency ?? "Monthly"} onChange={set("premium_frequency")} options={ref.data?.list("PayFrequency") ?? ["Monthly", "Annual"]} /></Field>
    <Field label="Premium pay period (how long the premium is paid)"><ListSelect list="PayPeriods" value={f.premium_pay_period} onChange={set("premium_pay_period")} placeholder="How long they pay"
      other={{ label: "Number of years", unit: "years", numeric: true }} /></Field>
  </>;
}
